<script setup lang="ts">
import { computed, onMounted, onUnmounted, provide, ref } from "vue";
import type { TemplaticalEditorConfig } from "./index";
import type { EditorTourStartOptions } from "./types/editor-tour";
import { useEditor } from "@aswin.dev/core";
import type { TemplateContent, UiTheme } from "@aswin.dev/types";
import {
  createDefaultPopupEmbedSettings,
  POPUP_SIZE_PRESET_WIDTH,
} from "@aswin.dev/types";
import { useEditorCore } from "./composables/useEditorCore";
import { resolveAccessibilityOptions } from "./utils/resolveAccessibilityOptions";
import type { Translations } from "./i18n";
import type { UseFontsReturn } from "./composables/useFonts";

import { RotateCcw } from "@lucide/vue";
import Canvas from "./components/Canvas.vue";
import CanvasPagesNav from "./components/CanvasPagesNav.vue";
import Sidebar from "./components/Sidebar.vue";
import RightSidebar from "./components/RightSidebar.vue";
import HistoryUndoRedo from "./components/HistoryUndoRedo.vue";
import ViewportToggle from "./components/ViewportToggle.vue";
import PreviewToggle from "./components/PreviewToggle.vue";
import DarkModeToggle from "./components/DarkModeToggle.vue";
import EditorFooter from "./components/EditorFooter.vue";
import EditorTour from "./components/EditorTour.vue";
import PopupDisplayRulesView from "./components/PopupDisplayRulesView.vue";
import PopupScheduleView from "./components/PopupScheduleView.vue";
import "./styles/index.css";
import { POPUP_RAIL_TAB_KEY, EDITOR_FLOATING_UI_TELEPORT_KEY } from "./keys";
import type { PopupRailTabId } from "./types/popup-rail-tab";
import { normalizeEditorType } from "./types/editor-type";
import {
  clearTourDismissedState,
  readTourDismissedState,
} from "./utils/tourStorage";

const editorTourRef = ref<InstanceType<typeof EditorTour> | null>(null);
const floatingUiTeleportEl = ref<HTMLElement | null>(null);
provide(EDITOR_FLOATING_UI_TELEPORT_KEY, floatingUiTeleportEl);
const props = defineProps<{
  config: TemplaticalEditorConfig;
  translations: Translations;
  fontsManager: UseFontsReturn;
}>();

// --- Core editor state ---
const editor = useEditor({
  content: props.config.content!,
  templateDefaults: props.config.templateDefaults,
});

// --- Shared editor core (composables, provides, plugins, keyboard) ---
const core = useEditorCore({
  editor,
  config: {
    uiTheme: props.config.uiTheme,
    theme: props.config.theme,
    blockDefaults: props.config.blockDefaults,
    customBlocks: props.config.customBlocks,
    mergeTags: props.config.mergeTags,
    displayConditions: props.config.displayConditions,
    onRequestMedia: props.config.onRequestMedia,
    editorType: props.config.editorType,
    accessibility: resolveAccessibilityOptions(props.config),
    onSave: props.config.onSave
      ? () =>
          props.config.onSave!(JSON.parse(JSON.stringify(editor.state.content)))
      : undefined,
  },
  translations: props.translations,
  fontsManager: props.fontsManager,
  autoSaveOptions: props.config.onChange
    ? {
        onChange: () =>
          props.config.onChange!(
            JSON.parse(JSON.stringify(editor.state.content)),
          ),
      }
    : null,
});

// --- Lifecycle ---
onMounted(async () => {
  await props.fontsManager.loadCustomFonts();
  if (
    normalizeEditorType(props.config.editorType) === "popup" &&
    !editor.content.value.settings.popup
  ) {
    const defaults = createDefaultPopupEmbedSettings();
    editor.updateSettings({
      popup: defaults,
      width: POPUP_SIZE_PRESET_WIDTH[defaults.design.sizePreset],
    });
  }
});

onUnmounted(() => {
  props.fontsManager.cleanupFontLinks();
  core.destroy();
});

const popupRailTab = ref<PopupRailTabId>("blocks");
provide(POPUP_RAIL_TAB_KEY, popupRailTab);

const isPopupEditor = computed(
  () => normalizeEditorType(props.config.editorType) === "popup",
);

const popupFullWorkspace = computed(
  () => isPopupEditor.value && popupRailTab.value === "displayRules",
);

const popupScheduleFullWorkspace = computed(
  () => isPopupEditor.value && popupRailTab.value === "schedule",
);

/** Design-only: 320px strip beside the canvas (not schedule — schedule uses full workspace). */
const popupWideAdjacentPanel = computed(() => popupRailTab.value === "design");

const popupHidesRightSidebar = computed(
  () =>
    isPopupEditor.value &&
    (popupFullWorkspace.value ||
      popupScheduleFullWorkspace.value ||
      popupWideAdjacentPanel.value),
);

/** Popup: left inset = 72px rail only (display rules / schedule), or + blocks/design strips when applicable. */
const editorChromeHorizontalInset = computed(() => {
  if (editor.state.previewMode) return ["tpl:left-0", "tpl:right-0"];
  if (!isPopupEditor.value) {
    return ["tpl:left-12", "tpl:right-[320px]"];
  }
  let left: string;
  if (popupRailTab.value === "blocks") {
    left = "tpl:left-[272px]";
  } else if (popupWideAdjacentPanel.value) {
    left = "tpl:left-[392px]";
  } else {
    left = "tpl:left-[72px]";
  }
  const right = popupHidesRightSidebar.value
    ? "tpl:right-0"
    : "tpl:right-[320px]";
  return [left, right];
});

const popupSettingsWorkspaceBg = computed(
  () => popupFullWorkspace.value || popupScheduleFullWorkspace.value,
);

// --- Public API (accessed via template ref from init()) ---
defineExpose({
  getContent: () => editor.content.value,
  setContent: (content: TemplateContent) => editor.setContent(content),
  setTheme: (theme: UiTheme) => editor.setUiTheme(theme),
  undo: () => core.history.undo(),
  redo: () => core.history.redo(),
  canUndo: () => core.history.canUndo.value,
  canRedo: () => core.history.canRedo.value,
  renderCustomBlock: core.registry.renderCustomBlock,
  startTour: (options?: EditorTourStartOptions) =>
    editorTourRef.value?.start(options),
  dismissTour: () => editorTourRef.value?.dismiss(),
  resetTourDismissed: () => clearTourDismissedState(props.config.tour),
  isTourDismissed: () =>
    props.config.tour ? readTourDismissedState(props.config.tour) : true,
});
</script>

<template>
  <div
    class="tpl tpl:relative tpl:h-full tpl:overflow-hidden"
    :class="{ 'tpl:dark': editor.state.darkMode }"
    :data-tpl-theme="core.resolvedTheme.value"
    :style="core.themeStyles.value"
  >
    <!-- Header — absolute, full width, above everything -->
    <header
      class="tpl-header tpl:absolute tpl:top-0 tpl:right-0 tpl:left-0 tpl:z-50 tpl:grid tpl:h-14 tpl:grid-cols-[1fr_auto_1fr] tpl:items-center tpl:px-4 tpl:shadow-[var(--tpl-shadow-md)] tpl:border-b tpl:border-[var(--tpl-border)]"
      style="
        background-color: color-mix(in srgb, var(--tpl-bg) 80%, transparent);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
      "
    >
      <!-- Left: empty (reserved for embedder customization) -->
      <div class="tpl:flex tpl:items-center tpl:gap-2.5"></div>

      <!-- Center: viewport + preview + dark mode -->
      <div class="tpl:flex tpl:items-center tpl:justify-center tpl:gap-10">
        <HistoryUndoRedo />
        <ViewportToggle
          :viewport="editor.state.viewport"
          @change="editor.setViewport"
        />
        <DarkModeToggle
          :dark-mode="editor.state.darkMode"
          @change="editor.setDarkMode"
        />
        <PreviewToggle
          :preview-mode="editor.state.previewMode"
          @change="editor.setPreviewMode"
        />
      </div>

      <!-- Right: empty in OSS mode -->
      <div
        class="tpl:flex tpl:min-w-[200px] tpl:items-center tpl:justify-end tpl:gap-3"
      ></div>
    </header>

    <!-- Left sidebar — absolute, below header -->
    <Sidebar v-show="!editor.state.previewMode" />

    <!-- Canvas area — absolute, fills remaining space -->
    <div
      class="tpl-body tpl:absolute tpl:bottom-0 tpl:overflow-auto"
      :style="{
        transition: 'all 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        backgroundColor: popupSettingsWorkspaceBg
          ? 'var(--tpl-bg)'
          : 'var(--tpl-canvas-bg)',
      }"
      :class="[...editorChromeHorizontalInset, 'tpl:top-14']"
    >
      <template v-if="isPopupEditor && popupRailTab === 'displayRules'">
        <PopupDisplayRulesView />
      </template>
      <template v-else-if="isPopupEditor && popupRailTab === 'schedule'">
        <PopupScheduleView layout="standalone" />
      </template>
      <template v-else>
        <!-- Restore hidden blocks button -->
        <div class="tpl:sticky tpl:top-0 tpl:z-40 tpl:h-0">
          <Transition name="tpl-restore-btn">
            <button
              v-if="core.conditionPreview.hasHiddenBlocks.value"
              class="tpl:absolute tpl:left-1/2 tpl:top-2 tpl:-translate-x-1/2 tpl:inline-flex tpl:items-center tpl:gap-1.5 tpl:rounded-full tpl:border tpl:px-3.5 tpl:py-1.5 tpl:text-xs tpl:font-medium tpl:whitespace-nowrap tpl:shadow-md tpl:hover:opacity-80 tpl:bg-[var(--tpl-warning-light)] tpl:text-[var(--tpl-warning)] tpl:border-[var(--tpl-warning)]"
              style="backdrop-filter: blur(8px)"
              @click="core.conditionPreview.reset()"
            >
              <RotateCcw :size="13" :stroke-width="2" />
              {{ core.t.blockSettings.restoreHiddenBlocks }}
            </button>
          </Transition>
        </div>
        <!-- Popup editor: full-width browser chrome, with steps strip above it -->
        <div
          v-if="isPopupEditor"
          class="tpl:flex tpl:w-full tpl:flex-col tpl:gap-3 tpl:px-4 tpl:pt-4 tpl:pb-6"
        >
          <CanvasPagesNav
            :preview-mode="editor.state.previewMode"
            :multi-page-canvas="config.multiPageCanvas ?? false"
          />
          <div
            class="tpl-popup-browser-frame tpl:flex tpl:min-h-[calc(100vh-11rem)] tpl:w-full tpl:flex-col tpl:overflow-hidden tpl:rounded-xl tpl:border tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg)] tpl:shadow-[var(--tpl-shadow-xl)]"
          >
            <div
              class="tpl-popup-browser-bar tpl:relative tpl:flex tpl:h-9 tpl:items-center tpl:gap-1.5 tpl:border-b tpl:border-[var(--tpl-border)] tpl:bg-[var(--tpl-bg-elevated)] tpl:px-3"
              aria-hidden="true"
            >
              <span
                class="tpl:block tpl:size-3 tpl:rounded-full"
                style="background-color: #ff5f57"
              />
              <span
                class="tpl:block tpl:size-3 tpl:rounded-full"
                style="background-color: #febc2e"
              />
              <span
                class="tpl:block tpl:size-3 tpl:rounded-full"
                style="background-color: #28c840"
              />
              <div
                class="tpl:pointer-events-none tpl:absolute tpl:left-1/2 tpl:top-1/2 tpl:-translate-x-1/2 tpl:-translate-y-1/2 tpl:rounded-md tpl:bg-[var(--tpl-bg)] tpl:px-3 tpl:py-0.5 tpl:text-[11px] tpl:font-medium tpl:text-[var(--tpl-text-muted)] tpl:tracking-tight"
              >
                yourstore.com
              </div>
            </div>
            <Canvas
              class="tpl:flex tpl:min-h-0 tpl:flex-1 tpl:flex-col"
              :viewport="editor.state.viewport"
              :content="editor.content.value"
              :selected-block-id="editor.state.selectedBlockId"
              :dark-mode="editor.state.darkMode"
              :preview-mode="editor.state.previewMode"
              :multi-page-canvas="config.multiPageCanvas ?? false"
              @select-block="editor.selectBlock"
            />
          </div>
        </div>

        <!-- Email editor: original centered canvas (with internal step strip from CanvasPagesNav) -->
        <div
          v-else
          class="tpl:flex tpl:w-full tpl:flex-col tpl:items-center tpl:gap-3 tpl:p-8"
        >
          <CanvasPagesNav
            :preview-mode="editor.state.previewMode"
            :multi-page-canvas="config.multiPageCanvas ?? false"
          />
          <Canvas
            :viewport="editor.state.viewport"
            :content="editor.content.value"
            :selected-block-id="editor.state.selectedBlockId"
            :dark-mode="editor.state.darkMode"
            :preview-mode="editor.state.previewMode"
            :multi-page-canvas="config.multiPageCanvas ?? false"
            @select-block="editor.selectBlock"
          />
        </div>
      </template>
    </div>

    <EditorFooter
      v-if="config.branding !== false"
      :position-class="editorChromeHorizontalInset"
    />

    <!-- Keyboard reorder announcement region (visually hidden, screen-reader live) -->
    <div
      class="tpl-sr-only"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      :aria-label="core.t.landmarks.reorderAnnouncements"
    >
      {{ core.keyboardReorder.announcement.value }}
    </div>

    <!-- Right sidebar — persisted with v-show -->
    <RightSidebar
      v-show="!editor.state.previewMode && !popupHidesRightSidebar"
      :selected-block="editor.selectedBlock.value"
      :settings="editor.content.value.settings"
      @update-block="
        (updates) => editor.updateBlock(editor.state.selectedBlockId!, updates)
      "
      @delete-block="
        () => {
          if (editor.state.selectedBlockId) {
            core.blockActions.deleteBlock(editor.state.selectedBlockId);
          }
        }
      "
      @duplicate-block="
        () => {
          if (editor.selectedBlock.value) {
            core.blockActions.duplicateBlock(editor.selectedBlock.value);
          }
        }
      "
      @update-settings="(updates) => editor.updateSettings(updates)"
    />

    <!-- Paragraph/title toolbars teleport here so they sit above popup canvas layers -->
    <div
      ref="floatingUiTeleportEl"
      class="tpl-editor-floating-ui tpl:pointer-events-none tpl:fixed tpl:inset-0 tpl:z-[9999] tpl:overflow-visible"
    />

    <EditorTour
      v-if="config.tour"
      ref="editorTourRef"
      :tour-config="config.tour"
      :dark-mode="editor.state.darkMode"
    />
  </div>
</template>

<style scoped>
.tpl-restore-btn-enter-active {
  transition:
    opacity 200ms cubic-bezier(0.16, 1, 0.3, 1),
    transform 200ms cubic-bezier(0.16, 1, 0.3, 1);
}

.tpl-restore-btn-leave-active {
  transition:
    opacity 150ms ease-in,
    transform 150ms ease-in;
}

.tpl-restore-btn-enter-from,
.tpl-restore-btn-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.9);
}

.tpl-restore-btn-enter-to,
.tpl-restore-btn-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}
</style>
